import { Box, Stack, Text, useMantineTheme } from '@mantine/core';
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { getMultiGoalStatus, getThresholdGoalState } from '../../statusUtils';
import type { TuAswpData } from '../types';
import { latest, pctChange, previous } from '../helpers';
import { CardShell } from './CardShell';
import { MetricRow } from './MetricRow';

type GraduatesCardProps = { data: TuAswpData };

export function GraduatesCard({ data }: GraduatesCardProps) {
  const theme = useMantineTheme();
  const series = data.graduates;
  const cur = series.length > 0 ? latest(series) : null;
  const prev = previous(series);

  const goalAllTime = 100; // Goal: 100 total by Y5Q4
  const allTime = data.graduatedAllTimeTotal;

  const totalDeclared = data.declaredAllTimeTotal;
  const completionRate = totalDeclared > 0 ? (allTime / totalDeclared) * 100 : 0;

  const qoq = cur ? pctChange(cur.graduated, prev?.graduated ?? null) : null;
  const arrow = (n: number): '↑' | '↓' | '' => (n > 0 ? '↑' : n < 0 ? '↓' : '');
  const qoqText = qoq === null ? '—' : arrow(qoq) ? `${arrow(qoq)}${Math.abs(qoq).toFixed(2)}% QoQ` : '0.00% QoQ';

  const parseQuarter = (label: string): { year: number; quarter: number } | null => {
    const match = label.match(/^Q([1-4])\s+(\d{4})$/);
    if (!match) return null;
    return { year: Number(match[2]), quarter: Number(match[1]) };
  };

  const currentMeta = parseQuarter(cur?.quarter ?? '');
  const ytd = currentMeta
    ? series
        .filter((point) => {
          const meta = parseQuarter(point.quarter);
          return meta?.year === currentMeta.year && meta.quarter <= currentMeta.quarter;
        })
        .reduce((sum, point) => sum + point.graduated, 0)
    : 0;

  const yoy = (() => {
    if (!currentMeta) return null;
    const prevYearTotal = series
      .filter((point) => {
        const meta = parseQuarter(point.quarter);
        return meta?.year === currentMeta.year - 1 && meta.quarter <= currentMeta.quarter;
      })
      .reduce((sum, point) => sum + point.graduated, 0);
    return pctChange(ytd, prevYearTotal);
  })();
  const yoyText = yoy === null ? '— YoY' : yoy === 0 ? '0.00% YoY' : `${arrow(yoy)}${Math.abs(yoy).toFixed(2)}% YoY`;

  const status = getMultiGoalStatus([
    getThresholdGoalState({ value: allTime, goal: goalAllTime, direction: 'min' }),
  ]);

  const tooltipShellStyle = {
    backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
    border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
    borderRadius: 10,
    color: theme.colorScheme === 'dark' ? '#fff' : '#000',
    padding: '10px 12px',
    maxWidth: 320,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  } as const;

  const maxGraduatedTotal = Math.max(goalAllTime, ...series.map((point) => point.graduatedTotal));

  return (
    <CardShell
      title="Individuals Graduated"
      subtitle={cur?.quarter ?? 'No data'}
      statusLabel={status.label}
      statusColor={status.color}
      statusTooltip={status.tooltip}
    >
      <Stack spacing="sm">
        <MetricRow
          items={[
            {
              label: 'This Quarter',
              labelTooltip: 'Graduates this quarter (AI minor completions).',
              value: (
                <Text size="lg" weight={800}>
                  {cur?.graduated ?? '—'}
                </Text>
              ),
              sublabel: qoqText,
            },
            {
              label: 'YTD',
              labelTooltip: 'Year-to-date graduates (sum of quarters in the current year through this quarter).',
              value: (
                <Text size="lg" weight={800}>
                  {ytd}
                </Text>
              ),
              sublabel: yoyText,
            },
            {
              label: 'Graduates (All-Time)',
              labelTooltip: `Total graduates across all time (includes periods not shown). Goal: ${goalAllTime} by Y5Q4 (25/year).`,
              value: (
                <Text size="lg" weight={800}>
                  {allTime}
                </Text>
              ),
              sublabel: `Goal ${goalAllTime}`,
            },
            {
              label: 'Completion Rate (All-Time)',
              labelTooltip: 'Percent of declared minors who graduated (all-time).',
              value: (
                <Text size="lg" weight={800}>
                  {Math.round(completionRate)}%
                </Text>
              ),
            },
          ]}
        />

        <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2, marginTop: '0.75rem' }}>
          Cumulative Graduates
        </Text>

        <Box sx={{ height: 170 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.18} vertical={false} />
              <XAxis dataKey="quarter" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={12} />
              <YAxis
                orientation="right"
                tick={{ fontSize: 11 }}
                allowDecimals={false}
                axisLine={false}
                tickLine={false}
                width={44}
                tickMargin={8}
                domain={[0, maxGraduatedTotal]}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  const p: any = payload[0]?.payload;
                  if (!p) return null;
                  return (
                    <div style={tooltipShellStyle as any}>
                      <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>{label}</div>
                      <div style={{ fontSize: 12, lineHeight: 1.35 }}>
                        <div style={{ fontWeight: 900, fontSize: 14 }}>{p.graduatedTotal} Total</div>
                        <div style={{ opacity: 0.9 }}>{p.graduated} graduated this quarter</div>
                      </div>
                    </div>
                  );
                }}
              />
              <ReferenceLine y={goalAllTime} stroke="#fa5252" strokeDasharray="4 4" />
              <Line
                type="monotone"
                dataKey="graduatedTotal"
                name="Cumulative Graduates"
                stroke={theme.colors.blue[6]}
                strokeWidth={2}
                dot={(dotProps: any) => {
                  const pointKey = dotProps?.payload?.quarter ?? dotProps?.index ?? `${dotProps?.cx}-${dotProps?.cy}`;
                  return (
                    <circle
                      key={`tuaswp-graduates-dot-${pointKey}`}
                      cx={dotProps.cx}
                      cy={dotProps.cy}
                      r={2}
                      fill={theme.colors.blue[6]}
                      stroke={theme.colorScheme === 'dark' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)'}
                      strokeWidth={1.5}
                    />
                  );
                }}
                activeDot={{ r: 4 }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Stack>
    </CardShell>
  );
}
